import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectorRef, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';

// Increasing integer for generating unique ids for checkbox components.
let nextUniqueId = 0;

/** Change event object emitted by CheckboxComponent */
export class CheckboxChange {
    constructor(
        /** The RadioComponentDef that emits the change event. */
        public source: CheckboxComponentDef,
        /** The value of the RadioComponentDef. */
        public value: any
    ) {}
}

import { Directive } from '@angular/core';
@Directive()
export abstract class CheckboxComponentDef implements ControlValueAccessor, OnDestroy {
    // tslint:disable-next-line: variable-name
    static ngAcceptInputType_disabled: BooleanInput;
    // tslint:disable-next-line: variable-name
    static ngAcceptInputType_required: BooleanInput;

    protected readonly _uniqueId: string = `checkbox-${++nextUniqueId}`;

    protected _disabled: boolean = false;
    protected _required: boolean;
    protected _checked: boolean = false;

    value: any;

    /** Returns the unique id for the visual hidden input. */
    get inputId(): string {
        return `${this.id || this._uniqueId}-input`;
    }

    @Input()
    checkedValue = true;

    /**
     * The form control value when the checkbox is unchecked.
     */
    @Input()
    notCheckedValue = false;

    /**
     * Whether the checkbox is disabled.
     */
    @Input()
    get disabled() {
        return this._disabled;
    }
    set disabled(value: any) {
        const newValue = coerceBooleanProperty(value);

        if (newValue !== this.disabled) {
            this._disabled = newValue;
            this.changeDetectorRef.detectChanges();
        }
    }

    /** Whether the checkbox is required. */
    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
    }

    /**
     * Whether the checkbox is checked.
     */
    @Input()
    get checked(): boolean {
        return this._checked;
    }
    set checked(value: boolean) {
        const newValue = coerceBooleanProperty(value);

        if (newValue !== this._checked) {
            this._checked = newValue;
            this.changeDetectorRef.detectChanges();
        }
    }
    /**
     * Attached to the aria-label attribute of the host element. In most cases, aria-labelledby will
     * take precedence so this may be omitted.
     */
    @Input('aria-label')
    ariaLabel: string = '';

    /**
     * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
     */
    @Input('aria-labelledby')
    ariaLabelledby: string | null = null;

    /** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
    @Input()
    id: string = this._uniqueId;

    /** Name value will be applied to the input element if present */
    @Input()
    name: string | null = null;

    /** The native `<input type="checkbox">` element */
    @ViewChild('input')
    inputElement: ElementRef<HTMLInputElement>;

    /**
     * Model change output to support two way binding
     */
    @Output()
    readonly change: EventEmitter<CheckboxChange> = new EventEmitter<CheckboxChange>();
    @Output()
    readonly checkedChange = new EventEmitter<boolean>();

    constructor(
        protected elementRef: ElementRef<HTMLElement>,
        protected changeDetectorRef: ChangeDetectorRef,
        protected focusMonitor: FocusMonitor,
        protected ref: ChangeDetectorRef
    ) {
        this.focusMonitor.monitor(elementRef, true).subscribe(focusOrigin => {
            if (!focusOrigin) {
                // When a focused element becomes disabled, the browser *immediately* fires a blur event.
                // Angular does not expect events to be raised during change detection, so any state change
                // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
                // See https://github.com/angular/angular/issues/17793. To work around this, we defer
                // telling the form control it has been touched until the next tick.
                Promise.resolve().then(() => {
                    this.onTouched();
                    changeDetectorRef.detectChanges();
                });
            }
        });
    }

    ngOnDestroy() {
        this.focusMonitor.stopMonitoring(this.elementRef);
    }

    /** Focuses the checkbox. */
    focus(origin: FocusOrigin = 'keyboard', options?: FocusOptions): void {
        this.focusMonitor.focusVia(this.inputElement, origin, options);
    }

    /**
     * The registered callback function called when a blur event occurs on the input element.
     */
    onTouched = () => {};

    /**
     * The registered callback function called when an input event occurs on the input element.
     */
    onChange = (_: any) => {};

    // Implemented as part of ControlValueAccessor.
    writeValue(value: any): void {
        this.update(value);
    }

    // Implemented as part of ControlValueAccessor.
    registerOnChange(fn: (value: any) => void) {
        this.onChange = fn;
    }

    // Implemented as part of ControlValueAccessor.
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    // Implemented as part of ControlValueAccessor.
    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    protected emitChangeEvent() {
        this.change.emit(new CheckboxChange(this, this.checked));
        this.checkedChange.emit(this.checked);
    }

    getAriaChecked(): 'true' | 'false' {
        if (this.checked) {
            return 'true';
        }

        return 'false';
    }

    /**
     * Event handler for checkbox input element.
     * Toggles checked state if element is not disabled.
     * Do not toggle on (change) event since IE doesn't fire change event when
     *   indeterminate checkbox is clicked.
     * @param event
     */
    onInputChange(event) {
        const newValue = event.target.checked ? this.checkedValue : this.notCheckedValue;
        if (!this.disabled) {
            this.update(newValue);
        }
    }

    private update(newValue: any): void {
        this.checked = newValue === this.checkedValue;
        this.value = newValue;
        this.onChange(this.value);
        this.ref.detectChanges();
        this.emitChangeEvent();
    }
}
